import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  AlertTriangle, UserX, IndianRupee, Calendar, FileText,
  LogOut, Phone, Building2, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WithdrawnInfo() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  // Get tenant code from URL or localStorage
  const getTenantCode = () => {
    const pathParts = window.location.pathname.split('/');
    if (pathParts.length > 2 && pathParts[2] === 'student') {
      return pathParts[1];
    }
    return localStorage.getItem('student_tenant_code') || '';
  };
  
  const tenantCode = getTenantCode();
  const basePath = tenantCode ? `/${tenantCode}` : '';

  useEffect(() => {
    const fetchWithdrawalInfo = async () => {
      const token = localStorage.getItem('studentToken');
      if (!token) {
        navigate(`${basePath}/student/login`);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/student-portal/withdrawal-info`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setInfo(data);
        } else if (response.status === 401) {
          localStorage.removeItem('studentToken');
          navigate(`${basePath}/student/login`);
        } else {
          const errData = await response.json();
          setError(errData.detail || 'Failed to load withdrawal info');
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load withdrawal information');
      } finally {
        setLoading(false);
      }
    };

    fetchWithdrawalInfo();
  }, [navigate, basePath]);

  const handleLogout = () => {
    localStorage.removeItem('studentToken');
    localStorage.removeItem('studentInfo');
    navigate(`${basePath}/student/login`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
            <Button onClick={handleLogout} className="mt-4">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const details = info?.withdrawal_details;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-800">Student Portal</h1>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Withdrawal Notice Card */}
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <UserX className="w-10 h-10 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-red-800">Admission Withdrawn</h2>
                <p className="text-red-600 mt-1">{info?.message}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Student Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> Student Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-semibold">{info?.student_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Student ID</p>
                <p className="font-semibold">{info?.student_id || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Course</p>
                <p className="font-semibold">{info?.course_name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {details ? (
          <>
            {/* Withdrawal Details */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Withdrawal Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Withdrawal No</p>
                    <p className="font-semibold text-red-600">{details.withdrawal_no || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-semibold">{details.withdrawal_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Reason</p>
                    <p className="font-semibold">{details.reason || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Processed By</p>
                    <p className="font-semibold">{details.processed_by || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fee Breakdown - What Was Paid */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-blue-600" /> Fee Paid (Before Withdrawal)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Seat Booking</p>
                    <p className="text-lg font-bold">₹{(details.fee_breakdown?.seat_booking || 0).toLocaleString('en-IN')}</p>
                    <span className="text-xs text-red-500">Non-refundable</span>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Admission Fee</p>
                    <p className="text-lg font-bold">₹{(details.fee_breakdown?.admission_fee_paid || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Semester Fee</p>
                    <p className="text-lg font-bold">₹{(details.fee_breakdown?.semester_fee_paid || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg text-center">
                    <p className="text-xs text-gray-500">Caution Money</p>
                    <p className="text-lg font-bold">₹{(details.fee_breakdown?.caution_money || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Refund Summary */}
            <Card className="mb-6 border-green-200">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <IndianRupee className="w-5 h-5" /> Refund Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {/* Refund Amounts */}
                  <div className="grid grid-cols-3 gap-4 pb-3 border-b">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Caution Money</p>
                      <p className="font-semibold text-green-600">₹{(details.refund_amounts?.caution_money || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Admission Fee</p>
                      <p className="font-semibold text-green-600">₹{(details.refund_amounts?.admission_fee || 0).toLocaleString('en-IN')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Semester Fee</p>
                      <p className="font-semibold text-green-600">₹{(details.refund_amounts?.semester_fee || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {/* Gross Refund */}
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">Gross Refund</span>
                    <span className="font-semibold">₹{(details.gross_refund || 0).toLocaleString('en-IN')}</span>
                  </div>

                  {/* Deductions */}
                  {details.deductions && details.deductions.length > 0 && (
                    <div className="bg-red-50 p-3 rounded-lg">
                      <p className="text-sm font-medium text-red-700 mb-2">Deductions:</p>
                      {details.deductions.map((d, idx) => (
                        <div key={idx} className="flex justify-between text-sm text-red-600">
                          <span>{d.name}</span>
                          <span>- ₹{(d.amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-medium text-red-700 border-t border-red-200 mt-2 pt-2">
                        <span>Total Deductions</span>
                        <span>- ₹{(details.total_deductions || 0).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  )}

                  {/* Net Refund */}
                  <div className="flex justify-between items-center p-4 bg-green-100 rounded-lg">
                    <span className="text-lg font-semibold text-green-800">Net Refund Amount</span>
                    <span className="text-2xl font-bold text-green-700">₹{(details.net_refund || 0).toLocaleString('en-IN')}</span>
                  </div>

                  {/* Refund Mode */}
                  <div className="flex justify-between items-center text-sm text-gray-500 pt-2">
                    <span>Refund Mode</span>
                    <span className="font-medium">{details.refund_mode || '-'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card className="mb-6">
            <CardContent className="pt-6 text-center py-12">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <p className="text-gray-600">Withdrawal details not available.</p>
              <p className="text-sm text-gray-500 mt-2">Please contact the office for more information.</p>
            </CardContent>
          </Card>
        )}

        {/* Contact Info */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-medium text-blue-800">Need Help?</p>
                <p className="text-sm text-blue-600">
                  For any queries regarding your withdrawal or refund, please contact the college office.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
